import { createContext, useReducer, useCallback, useEffect } from "react";

import { sleep } from "@/utils/until";
import { infoApi } from "@/http";
import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  InItUserInfoParam,
  JWTContextType,
} from "./types";
import { merchantStroe } from "@/stores";

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

enum Types {
  INITIAL = "INITIAL",
  LOGIN = "LOGIN",
  REGISTER = "REGISTER",
  LOGOUT = "LOGOUT",
  CHECKEMAIL = "CHECKEMAIL",
  WALLETINITIAL = "WALLETINITIAL",
  WALLETLOGIN = "WALLETLOGIN",
  WALLETREGISTER = "WALLETREGISTER",
  WALLETLOGOUT = "WALLETLOGOUT",
}

export enum SafetyMode {
  "none" = 0,
  email = 1,
  google = 2,
  eg = 3,
  phone = 4,
  ep = 5,
  gp = 6,
  egp = 7,
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    isVerify: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;

  [Types.CHECKEMAIL]: {
    user: any;
    isNewUser: boolean;
  };

  // WALLET API
  [Types.WALLETINITIAL]: {
    isAuthenticated: boolean;
    isVerify: boolean;
    user: any;
  };
  [Types.WALLETLOGIN]: {
    user: any;
  };
  [Types.WALLETREGISTER]: {
    user: any;
  };
  [Types.WALLETLOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  isVerify: false,
  isNewUser: false,
  user: {
    role_id: -1,
    create_time: 0,
    /** 用户邮箱 **/
    email: "",
    /** 是否开启google 2fa **/
    exists_google_2fa: false,
    /** 是否锁定登录 **/
    exists_lock_pass: false,
    /** 是否开启密码登录 **/
    exists_login_pass: true,
    /** 是否设置支付密码 **/
    exists_pay_pass: false,
    /** google 2fa的密钥 **/
    gg_verify_secret_key: "",
    /** google 2fa的二维码 **/
    gg_verify_secret_qr: "",
    /** 用户头像 **/
    head_url: "",
    id: 0,
    /** 14天无理由， fasle 需要 true 不需要 **/
    is_check: true,
    /** 密码锁定的次数 **/
    lock_try: 0,
    /** 无操作锁定时间 2-10min 3-30min 4-1hour 5-24hour **/
    lock_unit: 2,
    nickname: "",
    /** 用户电话 **/
    phone: "",
    phone_area: "",
    user_id: "",
    wwl_status: 0,
    security_email: 0,
    security_phone: 0,
    security_withdraw: 0,
    mode_type: 1,
    getAddressNum: 0,
    packageLimit: 0,
    emailNotify: 0,
    /** 商户是否开启了2fa验证 */
    safety2faStatus: 1,
    webhookNotifyMode: 1,
    ipWhitelist: "",
    webhookUrl: "",
    /** 账号的状态 */
    status: 1,
    isMaster: 0,
    /** 商户的的状态 */
    merchantStatus: 0,
    safetyMode: 0,
    merchantId: "",
    dispalyId: "",
    title: "",
    website_hash: "",
    company_website: "",
    company_address: "",
    twitter_url: "",
    telegram_url: "",
    company_industry: "",
    company_industry_text: "",
    main_functions: "",
    main_functions_text: "",
    is_pay_passwd: false,
    is_security: false,
    test_coin: -1,
    robot_txt: "",
    member_num: 0,
    username: "",
    version: 1,
    auto_add_coin: -1,
    withdraw_approve: -1,
    withdraw_approve_setting: {
      limit_types: [],
      limit_qty: "0",
      ttl: 1,
      safety_mode: 0,
    },
    is_newbie: "-1",
    lpw_status: -1,
    login_ip_whitelist: "",
    wiw_status: -1,
    withdraw_ip_whitelist: "",
    oiw_status: -1,
    owner_ip_whitelist: "",
  },
};

const setMerchantIdObj = (user: AuthUserType) => {
  if (user?.mode_type === 1) {
    if (user?.main_merchant_id && user?.merchant_id) {
      const obj = {
        walletMerchantId: user?.main_merchant_id,
        ccpMerchantId: user?.merchant_id,
      };
      localStorage.setItem("MerchantIdObj", JSON.stringify(obj));
    }
  }
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    setMerchantIdObj(action.payload.user);
    return {
      isInitialized: true,
      isNewUser: false,
      isVerify: action.payload.isVerify,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    setMerchantIdObj(action.payload.user);
    return {
      ...state,
      isAuthenticated: true,
      isVerify: false,
      isNewUser: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    setMerchantIdObj(action.payload.user);
    return {
      ...state,
      isAuthenticated: true,
      isVerify: true,
      isNewUser: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: false,
      isVerify: false,
      isNewUser: false,
      user: null,
    };
  }
  if (action.type === Types.CHECKEMAIL) {
    return {
      ...state,
      isNewUser: action.payload.isNewUser,
      isInitialized: true,
      user: action.payload.user,
    };
  }

  return state;
};

export const AuthContext = createContext<JWTContextType | null>(null);

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { setUserInfo } = merchantStroe;
  // ccp

  /**
   * Initializes the user information by fetching the user token and user info from the API.
   * If the user token is not available, it removes the Authorization item from localStorage.
   * If the user info is successfully fetched, it updates the user information in the state and returns the updated user object.
   * If there is an error, it handles the error by removing the Authorization item from localStorage or retrying the request after a 3-second delay.
   *
   * @param {InItUserInfoParam} params - An object containing options for the initialization process.
   * @param {boolean} [params.isNeedExtra=true] - Whether to fetch additional user information.
   * @param {boolean} [params.refreshRouter=false] - Whether to refresh the router after updating the user information.
   * @returns {Promise<AuthUserType>} - The updated user object.
   */
  const initUserInfo = async ({
    isNeedExtra = true,
    refreshRouter = false,
  }: InItUserInfoParam) => {
    try {
      const res_ = await infoApi.getUserToken();
      if (res_ && res_?.token) {
        localStorage.setItem("Authorization", JSON.stringify(res_.token));
      }

      if (!res_?.token) return;

      const { detail: res } = await infoApi.getUserInfo();
      setUserInfo(res);
      let extraData = {
        website_hash: "",
        company_website: "",
        company_address: "",
        twitter_url: "",
        telegram_url: "",
        company_industry: "",
        company_industry_text: "",
        main_functions: "",
        main_functions_text: "",
        robot_txt: "",
        is_newbie: "-1",
      };
      extraData = {
        website_hash: state.user?.website_hash || "",
        company_website: state.user?.company_website || "",
        company_address: state.user?.company_address || "",
        twitter_url: state.user?.twitter_url || "",
        telegram_url: state.user?.telegram_url || "",
        company_industry: state.user?.company_industry || "",
        company_industry_text: state.user?.company_industry_text || "",
        main_functions: state.user?.main_functions || "",
        main_functions_text: state.user?.main_functions_text || "",
        robot_txt: "",
        is_newbie: state.user?.is_newbie || "-1",
      };

      const pu: any = {
        ...initialState.user,
        email: res?.email,
        /** google 2fa的密钥 **/
        gg_verify_secret_key: res?.google_key || "",
        /** 用户头像 **/
        head_url: res?.logo || "",
        /** 无操作锁定时间 2-10min 3-30min 4-1hour 5-24hour **/
        lock_unit: res?.lock_unit || 0,
        nickname: res?.title || "",
        /** 用户电话 **/
        phone: res?.phone || "",
        phone_area: res?.phone_area || "",
        wwl_status: res?.wwl_status || -1,

        security_withdraw: res?.micro_withdraw === 1 ? 1 : 0,
        safetyMode: res?.safety_2fa | 0,
        mode_type: 1,
        getAddressNum: 0,
        packageLimit: 0,
        emailNotify: res?.email_notify || -1,
        /** 商户是否开启了2fa验证 */
        safety2faStatus: res?.safety_2fa_status || 1,
        webhookNotifyMode: res?.webhook_notify_mode || -1,
        ipWhitelist: "",
        webhookUrl: res?.webhook_url || "",
        /** 账号的状态 */
        status: res?.status || 1,
        isMaster: 0,
        /** 商户的的状态 */
        merchantStatus: res?.merchant_status || 1,
        merchantId: res?.merchant_id || "",
        dispalyId: res?.display_id || "",
        title: res.title,
        website_hash: extraData?.website_hash || "",
        company_website: extraData?.company_website || "",
        company_address: extraData?.company_address || "",
        twitter_url: extraData?.twitter_url || "",
        telegram_url: extraData?.telegram_url || "",
        company_industry: extraData?.company_industry || "",
        company_industry_text: extraData?.company_industry_text || "",
        main_functions: extraData?.main_functions || "",
        main_functions_text: extraData?.main_functions_text || "",
        is_pay_passwd: res.is_pay_passwd || false,
        is_security: false,
        auto_swap: res?.auto_swap || -1,
        test_coin: res?.test_coin || -1,
        robot_txt: extraData?.robot_txt || "",
        member_num: res?.member_num || 0,
        username: res?.username || "",
        role_id: res?.role_id || -1,
        version: res?.version || 1,
        app_id: res?.app_id || "",
        auto_add_coin: res?.auto_add_coin || -1,
        withdraw_approve: res?.withdraw_approve || -1,
        withdraw_approve_setting: res?.withdraw_approve_setting || {
          limit_types: [],
          limit_qty: "0",
          ttl: 1,
          safety_mode: 0,
        },
        is_newbie: extraData?.is_newbie || -1,
        lpw_status: -1,
        login_ip_whitelist: "",
        wiw_status: -1,
        withdraw_ip_whitelist: "",
        oiw_status: -1,
        owner_ip_whitelist: "",
      };

      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: true,
          isVerify: true,
          user: pu as AuthUserType,
        },
      });

      return pu;
    } catch (error) {
      console.log("error.message", error, error.message);

      if (error?.message?.includes(`Unexpected token 'e'`)) {
        localStorage.removeItem("Authorization");
        sessionStorage.setItem("directList", JSON.stringify([]));
      } else {
        await sleep(3000);
        initUserInfo({ isNeedExtra, refreshRouter });
      }
      // console.log({ error });
    }
  };

  // 更新用户信息
  const updUserInfo = useCallback(
    async (isAuthenticated: boolean, isVerify: boolean, user: AuthUserType) => {
      if (!isAuthenticated && !isVerify) {
        localStorage.removeItem("Authorization");
      }
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: isAuthenticated,
          isVerify: isVerify,
          user: user
            ? { ...user, mode_type: 1 }
            : ({ ...state.user } as AuthUserType),
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line
    []
  );

  const updUserInfoToStore = async ({
    isNeedExtra = true,
    refreshRouter = false,
  }: InItUserInfoParam) => initUserInfo({ isNeedExtra, refreshRouter });

  useEffect(() => {
    let isNeedExtra = true;
    let refreshRouter = false;
    initUserInfo({ isNeedExtra, refreshRouter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        updUserInfo,
        updUserInfoToStore,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
